<script>
import {ref, computed, toRef} from "vue";
import {IMaskDirective} from 'vue-imask';

import IconX from "@js/components/Icons/IconX.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconAnimatedCheckMark from "@js/components/Icons/IconAnimatedCheckMark.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import {useSwipePanel} from "@js/useSwipePanel.js";

export default {
    name: "FastBuyCallback",
    components: {
        NumberFormatComponent,
        ImageLoader,
        IconAnimatedCheckMark,
        OnlyMobile,
        OnlyDesktop,
        PrivacyPolicyAgreementLink,
        SpinnerIndicator,
        CallbackErrorMessage,
        IconX,
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            phoneMask: {
                mask: '+7 (000) 000-00-00',
            },
        }
    },
    methods: {
        updatePhone(newPhone) {
            this.productStore.callback.data.phone = newPhone;
        },
    },
    setup() {
        const productStore = useProductStore()

        const modalRef = ref(null)
        const dragHandleRef = ref(null)

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen: toRef(productStore.callback, 'isOpen'),
            panelRef: modalRef,
            handleRef: dragHandleRef,
            direction: 'bottom',
            closeThreshold: 110,
            onClose: () => {
                productStore.closeFastBuyPopup()
            },
        })

        const minPrice = computed(() => {
            const prices = productStore.callback.product?.prices || []

            const values = prices
                .map(item => Number(item.base_price || 0))
                .filter(v => v > 0)

            return values.length ? Math.min(...values) : null
        })

        const minPriceTypeShortTitle = computed(() => {
            const prices = productStore.callback.product?.prices || []

            const sorted = prices
                .filter(item => Number(item.base_price || 0) > 0)
                .sort((a, b) => Number(a.base_price) - Number(b.base_price))

            return sorted[0]?.price_type?.short_title ?? 'шт'
        })

        return {
            productStore,
            modalRef,
            dragHandleRef,
            rendered,
            panelStyle,
            backdropStyle,
            closeModal: close,
            handleBackgroundClick,
            minPrice,
            minPriceTypeShortTitle,
        }
    },
}
</script>

<template>
    <div
        v-if="rendered"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click="handleBackgroundClick"
    >
        <div
            class="absolute inset-0 bg-black/70"
            :style="backdropStyle"
        ></div>

        <div
            ref="modalRef"
            :style="panelStyle"
            class="relative z-[1] bg-white bg-contain bg-no-repeat bg-center rounded-tr-2xl rounded-tl-2xl sm:rounded-2xl shadow-lg max-h-[550px] sm:max-w-[450px] sm:max-h-[520px] w-full h-full overflow-hidden p-5"
            @click.stop
        >
            <button
                @click.prevent="closeModal"
                class="absolute top-2 right-2 text-gray-400 md:hover:text-white focus:outline-none z-20"
            >
                <IconX
                    class="h-7 w-7 stroke-[#000] active:stroke-baseColor sm:hover:stroke-baseColor transition"
                />
            </button>

            <div class="absolute top-3 right-0 left-0 m-auto z-10 flex justify-center lg:hidden">
                <div
                    ref="dragHandleRef"
                    class="h-8 w-24 flex items-start justify-center select-none"
                    style="touch-action: none; user-select: none;"
                >
                    <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
                </div>
            </div>

            <div class="w-full h-full flex flex-col items-start sm:items-center max-sm:mt-6 overflow-y-auto">
                <template v-if="!productStore.callback.successfully_sent">
                    <div class="w-full max-w-sm mx-auto mb-5">
                        <div class="text-xl font-semibold text-gray-700 mb-5">
                            Заявка на покупку товара
                        </div>

                        <div class="grid grid-cols-[100px_1fr] text-left gap-5">
                            <div class="w-[100px] h-auto p-1 border border-grayBg rounded-xl overflow-hidden">
                                <ImageLoader
                                    :src="'/storage/products/' + (productStore.callback.product?.media?.images?.[0] || '')"
                                    :title="productStore.callback.product?.title"
                                    imgClasses="w-full h-full object-contain"
                                />
                            </div>

                            <div class="flex flex-col gap-1">
                                <p class="text-base text-[#2D2D2D] font-medium">
                                    {{ productStore.callback.product?.title }}
                                </p>

                                <p v-if="minPrice !== null" class="font-bold">
                                    <NumberFormatComponent
                                        :number="minPrice"
                                        append_text=" ₽"
                                        prepend_text="Цена от: "
                                        class="text-sm text-gray-600"
                                    />
                                    <span class="font-medium text-sm text-gray-600">
                                        /{{ minPriceTypeShortTitle }}
                                    </span>
                                </p>

                                <p v-else class="font-bold text-sm text-gray-600">
                                    Цена: договорная
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="w-full max-w-sm mx-auto">
                        <div class="text-gray-700 font-semibold mb-3">
                            Данные покупателя
                        </div>

                        <div class="mb-5">
                            <div class="relative">
                                <input
                                    type="text"
                                    id="ptf_phone"
                                    @keyup="updatePhone($event.target.value)"
                                    :value="productStore.callback.data.phone"
                                    v-imask="phoneMask"
                                    class="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                    :class="[productStore.callback.errors && productStore.callback.errors['phone'] ? 'border-red-500' : '']"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    for="ptf_phone"
                                    class="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
                                >
                                    Номер телефона
                                </label>
                            </div>

                            <CallbackErrorMessage
                                :error="productStore.callback.errors"
                                :error-msg="'phone'"
                            />
                        </div>

                        <div class="mb-5">
                            <div class="relative">
                                <input
                                    type="text"
                                    id="ptf_full_name"
                                    v-model="productStore.callback.data.full_name"
                                    class="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                    :class="[productStore.callback.errors && productStore.callback.errors['full_name'] ? 'border-red-500' : '']"
                                    placeholder=" "
                                    required
                                />
                                <label
                                    for="ptf_full_name"
                                    class="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
                                >
                                    Как к вам обращаться?
                                </label>
                            </div>

                            <CallbackErrorMessage
                                :error="productStore.callback.errors"
                                :error-msg="'full_name'"
                            />
                        </div>

                        <div class="relative w-full lg:w-auto flex justify-center">
                            <button
                                @click.stop="productStore.sendPopupForm"
                                :disabled="productStore.callback.sending"
                                class="flex justify-center items-center w-full text-black border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-medium rounded-lg text-sm px-14 py-2.5 focus:outline-none transition cursor-pointer"
                            >
                                <span v-if="!productStore.callback.sending" class="text-base">
                                    Отправить заказ
                                </span>
                                <span v-else class="text-base">
                                    Отправляем
                                </span>
                                <SpinnerIndicator
                                    v-if="productStore.callback.sending"
                                    class="w-6 h-6 ms-2"
                                />
                            </button>
                        </div>

                        <div class="mt-5">
                            <PrivacyPolicyAgreementLink/>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="w-full max-w-sm mx-auto p-3 sm:p-5 flex flex-col items-center">
                        <IconAnimatedCheckMark size="80"/>
                        <p class="text-lg font-medium text-center text-[#7ac142] mt-5">
                            Спасибо!<br/> Мы скоро свяжемся с вами.
                        </p>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
